import React, {useRef, useEffect} from 'react';
import {
  TouchableOpacity,
  StyleSheet,
  Animated,
  ViewStyle,
} from 'react-native';
import {Icon} from './Icon';
import {useTheme} from '../hooks/useTheme';

interface BackToTopButtonProps {
  visible: boolean;
  onPress: () => void;
  bottomOffset?: number; // Additional offset from bottom (for tab bar, nav bar, etc.)
  style?: ViewStyle;
}

export const BackToTopButton: React.FC<BackToTopButtonProps> = ({
  visible,
  onPress,
  bottomOffset = 100,
  style,
}) => {
  const {colors, isDark} = useTheme();
  const opacity = useRef(new Animated.Value(0)).current;
  const scale = useRef(new Animated.Value(0.8)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: visible ? 1 : 0,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.spring(scale, {
        toValue: visible ? 1 : 0.8,
        friction: 8,
        useNativeDriver: true,
      }),
    ]).start();
  }, [visible, opacity, scale]);

  if (!visible) {
    return null;
  }

  return (
    <Animated.View
      style={[
        styles.container,
        {
          bottom: bottomOffset,
          opacity,
          transform: [{scale}],
        },
        style,
      ]}
      pointerEvents={visible ? 'auto' : 'none'}
    >
      <TouchableOpacity
        style={[
          styles.button,
          {
            backgroundColor: isDark ? colors.surface : '#FFFFFF',
            shadowColor: isDark ? '#000' : colors.primary,
          },
        ]}
        onPress={onPress}
        activeOpacity={0.8}
      >
        <Icon name="arrow-up" size={20} color={colors.primary} />
      </TouchableOpacity>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    right: 20,
    zIndex: 1000,
  },
  button: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 6,
    borderWidth: 1,
    borderColor: 'rgba(68, 18, 116, 0.1)',
  },
});

export default BackToTopButton;
